<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('saleByCustomerSummary.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('saleByCustomerSummary.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('saleByCustomerSummary.criteria') }}</legend>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('saleByCustomerSummary.date')
                      }}</label>
                      <a-range-picker
                        style="width: 100%"
                        v-model="model.date_range"
                        format="DD-MM-YYYY"
                        valueFormat="DD-MM-YYYY"
                      />
                      <div class="validate" v-if="errors.has('date_range')">
                        {{ errors.first('date_range') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('saleByCustomerSummary.branch')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        mode="multiple"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.branch_id"
                        :options="branchList"
                        placeholder="All"
                      ></a-select>
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('saleByCustomerSummary.customer') }}</label>
                      <a-select
                        style="width: 100%; font-size: 15px"
                        v-model="model.customer_id"
                        :options="customerLists"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        mode="multiple"
                        :maxTagCount="1"
                        :placeholder="$t('all')"
                        @search="handleSearchCustomer"
                        :loading="customer_loading"
                      ></a-select>
                      <div class="validate" v-if="errors.has('customer_id')">
                        {{ errors.first('customer_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('saleByCustomerSummary.status') }}</label>
                      <a-select
                        style="width: 100%"
                        v-model="model.status"
                        mode="multiple"
                        :options="paymentStatus"
                        placeholder="All"
                      >
                      </a-select>
                      <div class="validate" v-if="errors.has('status')">
                        {{ errors.first('status') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{
                        $t('saleByCustomerSummary.productGroup')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        mode="multiple"
                        showSearch
                        v-model="model.pro_group_id"
                        :options="productGroupList"
                        placeholder="All"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                      >
                      </a-select>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('saleByCustomerSummary.type') }}</label>
                      <a-select
                        style="width: 100%"
                        v-model="model.type"
                        mode="multiple"
                        :options="posType"
                        placeholder="All"
                      >
                      </a-select>
                      <div class="validate" v-if="errors.has('type')">
                        {{ errors.first('type') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('saleByCustomerSummary.report') }}</label>
                      <a-select
                        style="width: 100%"
                        v-model="model.report_type"
                        :options="reportTypes"
                      >
                      </a-select>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6 ts-text-right">
                      <ts-button
                        outline
                        color="success"
                        class="btn-block"
                        @click.prevent="exportExcel"
                        :waiting="exporting"
                        ><i class="far fa-file-excel"></i>
                        {{ $t('export') }}</ts-button
                      >
                    </div>
                    <div class="col-md-6 ts-text-right">
                      <ts-button
                        color="danger"
                        outline
                        class="btn-block"
                        @click.prevent="preview"
                        :waiting="waiting"
                        ><i class="far fa-file-pdf"></i>
                        {{ $t('preview') }}</ts-button
                      >
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('saleByCustomerSummary.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim, debounce } from 'lodash'

export default {
  name: 'saleByCustomerSummary',
  data () {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      customer_loading: false,
      src: null,
      errors: new Errors(),
      customers: [],
      branchList: [],
      productGroupList: [],
      model: {
        branch_id: [],
        customer_id: [],
        status: [],
        type: [],
        pro_group_id: [],
        date_range: [
          moment().format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY')
        ],
        report_type: 1
      }
    }
  },
  created () {
    this.fetchData()
    this.model.branch_id = [
      this.$store.state.authUser.user.default_branch.branch_id
    ]
  },
  computed: {
    paymentStatus () {
      return [
        { value: 'paid', label: 'Paid' },
        { value: 'partially paid', label: 'Partially Paid' },
        { value: 'unpaid', label: 'Unpaid' }
      ]
    },
    posType () {
      return [
        { value: 'RTS', label: 'Retail sale' },
        { value: 'WHS', label: 'Whole sale' },
        { value: 'BRW', label: 'Borrowing' }
      ]
    },
    reportTypes () {
      return [
        { value: 1, label: 'Summary' },
        { value: 2, label: 'Detail' }
      ]
    },
    customerLists () {
      return this.customers.map(el => ({
        value: el.customer_id,
        label: el.customer_name
      }))
    }
  },
  methods: {
    ...mapActions('report/resource', ['productGroup']),
    ...mapActions('report/finance', [
      'saleByCustomerSummary',
      'customerProfile',
      'saleByCustomerSummaryToExcel'
    ]),
    async fetchData () {
      this.loading = true
      await this.$store
        .dispatch('security/user/getBranchAssigned')
        .then(response => {
          this.branchList = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
      await this.$store
        .dispatch('report/resource/getCustomer')
        .then(response => {
          this.customers = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
      await this.productGroup()
        .then(response => {
          this.productGroupList = response.data.map(el => ({
            value: el.pro_group_id,
            label: el.pro_group_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSearchCustomer: debounce(async function (value) {
      if (!value) {
        return false
      } else {
        this.customer_loading = true
        await this.$store
          .dispatch('report/resource/getCustomer', {
            searchCustomerName: value
            // customerType: this.model.customer_type
          })
          .then(response => {
            this.customers = response.data
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
          .finally(() => {
            this.customer_loading = false
          })
      }
    }, 500),
    preview () {
      this.waiting = true
      this.src = ''
      this.errors = new Errors()
      this.saleByCustomerSummary(this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel () {
      this.exporting = true
      this.errors = new Errors()
      this.saleByCustomerSummaryToExcel(this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    }
  }
}
</script>
